import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "@fox1ck-org/ui/toast";
import { useTriggerProdToDev } from "@/hooks/use-backup";
import type { CNPGCluster } from "@/api/backup";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@fox1ck-org/ui";

interface Props {
  clusters: CNPGCluster[];
  onClose: () => void;
}

export function ProdToDevDialog({ clusters, onClose }: Props) {
  const prodClusters = clusters.filter((c) => !c.namespace.endsWith("-dev"));
  const devClusters = clusters.filter((c) => c.namespace.endsWith("-dev"));

  const [sourceCluster, setSourceCluster] = useState(prodClusters[0]?.name ?? "");
  const [sourceNamespace, setSourceNamespace] = useState(prodClusters[0]?.namespace ?? "");
  const [targetCluster, setTargetCluster] = useState(devClusters[0]?.name ?? "");
  const [targetNamespace, setTargetNamespace] = useState(devClusters[0]?.namespace ?? "");
  const [anonymize, setAnonymize] = useState(true);
  const [confirmed, setConfirmed] = useState(false);

  const trigger = useTriggerProdToDev();

  const handleSourceChange = (value: string) => {
    const c = prodClusters.find((c) => `${c.namespace}/${c.name}` === value);
    if (c) {
      setSourceCluster(c.name);
      setSourceNamespace(c.namespace);
      const devMatch = devClusters.find((d) => d.name === c.name || d.namespace === `${c.namespace}-dev`);
      if (devMatch) {
        setTargetCluster(devMatch.name);
        setTargetNamespace(devMatch.namespace);
      }
    }
  };

  const handleTargetChange = (value: string) => {
    const c = devClusters.find((c) => `${c.namespace}/${c.name}` === value);
    if (c) {
      setTargetCluster(c.name);
      setTargetNamespace(c.namespace);
    }
  };

  const handleSubmit = () => {
    trigger.mutate(
      { sourceCluster, sourceNamespace, targetCluster, targetNamespace, anonymize },
      {
        onSuccess: () => { toast.success("Перенос из прода в дев запущен"); onClose(); },
        onError: (err) => toast.error(err.message),
      }
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Перенос данных из прода в дев</DialogTitle>
          <DialogDescription>Копия боевой базы в кластер разработки.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Источник (прод)</label>
            <Select value={`${sourceNamespace}/${sourceCluster}`} onValueChange={handleSourceChange}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {prodClusters.map((c) => (
                  <SelectItem key={`${c.namespace}/${c.name}`} value={`${c.namespace}/${c.name}`}>
                    {c.name} ({c.namespace})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Цель (дев)</label>
            <Select value={`${targetNamespace}/${targetCluster}`} onValueChange={handleTargetChange}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {devClusters.length === 0 ? (
                  <SelectItem value="none" disabled>Кластеров разработки нет</SelectItem>
                ) : devClusters.map((c) => (
                  <SelectItem key={`${c.namespace}/${c.name}`} value={`${c.namespace}/${c.name}`}>
                    {c.name} ({c.namespace})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm text-foreground">Обезличить данные</label>
              <p className="text-xs text-muted-foreground">Убрать персональные данные перед копированием</p>
            </div>
            <button
              onClick={() => setAnonymize(!anonymize)}
              className={`w-10 h-6 rounded-full transition-colors ${anonymize ? "bg-primary" : "bg-muted"}`}
            >
              <div className={`w-4 h-4 bg-white rounded-full transition-transform mx-1 ${anonymize ? "translate-x-4" : ""}`} />
            </button>
          </div>

          {!anonymize && (
            <div className="flex items-start gap-2 rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3">
              <AlertTriangle className="h-4 w-4 text-yellow-400 mt-0.5 shrink-0" />
              <p className="text-xs text-yellow-400">
                Обезличивание отключено. Настоящие боевые данные (персональные данные, учётные данные) попадут в дев.
              </p>
            </div>
          )}

          <div className="flex items-center gap-2">
            <input type="checkbox" id="confirm-p2d" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="rounded" />
            <label htmlFor="confirm-p2d" className="text-sm text-muted-foreground">
              Понимаю, что данные в целевой базе дева будут заменены
            </label>
          </div>

          {trigger.error && <p className="text-sm text-destructive">{trigger.error.message}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Отмена</Button>
          <Button onClick={handleSubmit} disabled={!confirmed || !targetCluster || trigger.isPending} className="bg-orange-600 hover:bg-orange-500">
            {trigger.isPending ? "Запускаем…" : "Запустить перенос"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
